import { Send, Check, Smile, Timer } from "lucide-react";

// ── Crocodile Player Controller ───────────────────────────────────────────────
// Props:
//   isExplainer    : bool
//   word           : string   — secret word (only when isExplainer)
//   category       : string
//   explainer      : string   — name of who is explaining
//   guess          : string
//   setGuess       : (v) => void
//   timeLeft       : number
//   guessedBy      : string[]
//   myGuessCorrect : bool
//   roundEnd       : object | null
//   onGuess        : () => void
//   onSkip         : () => void
//   playerName     : string

export default function CrocodilePlayer({
  isExplainer, word, category, explainer,
  guess, setGuess, timeLeft, guessedBy,
  myGuessCorrect, roundEnd, onGuess, onSkip, playerName,
}) {
  const handleSubmit = (e) => {
    e.preventDefault();
    onGuess();
  };

  const timerColor = timeLeft > 30 ? "#39FF14" : timeLeft > 10 ? "#FFFF00" : "#FF0055";

  // Round ended
  if (roundEnd) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center animate-slide-up gap-4">
        <Smile className="w-16 h-16 text-[#39FF14] mx-auto" />
        <h2 className="font-heading text-2xl text-white">
          {roundEnd.skipped ? "Слово пропущено!" : roundEnd.time_expired ? "Время вышло!" : "Раунд окончен!"}
        </h2>
        <div className="card-cartoon px-8 py-4">
          <p className="text-[#A1A1AA] text-sm mb-1">Загаданное слово:</p>
          <p className="font-heading text-3xl text-[#FFFF00]">{roundEnd.word}</p>
        </div>
        {roundEnd.guessed_by.length > 0
          ? <p className="text-[#39FF14]">Угадали: {roundEnd.guessed_by.join(", ")}</p>
          : <p className="text-[#A1A1AA]">Никто не угадал 😅</p>
        }
        <p className="text-[#A1A1AA] text-sm">Следующий раунд...</p>
      </div>
    );
  }

  // Explainer screen
  if (isExplainer) {
    return (
      <div className="flex-1 flex flex-col animate-slide-up">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Timer className="w-5 h-5" style={{ color: timerColor }} />
          <span className="font-display text-3xl" style={{ color: timerColor }}>{timeLeft}</span>
        </div>

        <div className="card-cartoon text-center mb-6">
          <p className="text-[#A1A1AA] text-sm mb-1">ОБЪЯСНИ БЕЗ СЛОВ:</p>
          <h2 className="font-heading text-3xl text-[#FFFF00] neon-yellow mb-2">{word}</h2>
          {category && (
            <span className="text-xs text-[#A1A1AA] bg-[#2D2D3A] px-3 py-1 rounded-full">{category}</span>
          )}
        </div>

        <div className="bg-black/40 border-2 border-black/50 rounded-xl p-4 mb-6 text-sm text-[#ccc] space-y-1">
          <p>🚫 Нельзя называть само слово</p>
          <p>🚫 Нельзя называть однокоренные слова</p>
          <p>✅ Можно жесты, мимику, описание</p>
        </div>

        {guessedBy.length > 0 && (
          <div className="mb-4 text-center animate-bounce-in">
            <p className="text-[#39FF14] font-bold">✅ Угадали: {guessedBy.join(", ")}</p>
          </div>
        )}

        <div className="mt-auto">
          <button onClick={onSkip} data-testid="crocodile-skip-btn" className="btn-cartoon btn-dark w-full py-4">
            Пропустить слово
          </button>
        </div>
      </div>
    );
  }

  // Guesser screen
  return (
    <div className="flex-1 flex flex-col animate-slide-up">
      <div className="flex items-center justify-center gap-2 mb-2">
        <Timer className="w-5 h-5" style={{ color: timerColor }} />
        <span className="font-display text-3xl" style={{ color: timerColor }}>{timeLeft}</span>
      </div>

      <div className="text-center mb-4">
        <p className="text-[#A1A1AA] text-sm">Объясняет:</p>
        <h2 className="font-heading text-2xl text-[#FF00FF]">{explainer}</h2>
        {category && (
          <span className="text-xs text-[#A1A1AA] bg-[#2D2D3A] px-3 py-1 rounded-full mt-1 inline-block">
            Категория: {category}
          </span>
        )}
      </div>

      {myGuessCorrect ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center animate-bounce-in">
            <Check className="w-16 h-16 text-[#39FF14] mx-auto mb-3" />
            <p className="font-heading text-2xl text-[#39FF14]">Ты угадал!</p>
            <p className="text-[#A1A1AA] mt-2">+100 очков 🎉</p>
          </div>
        </div>
      ) : (
        <>
          {guessedBy.length > 0 && (
            <div className="mb-3 text-center">
              <p className="text-[#39FF14] text-sm">✅ Угадали: {guessedBy.join(", ")}</p>
            </div>
          )}
          <div className="flex-1 flex items-center justify-center">
            <p className="text-[#A1A1AA] text-center">Смотри на объясняющего и угадывай!</p>
          </div>
          <div className="mt-auto">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <input
                type="text"
                value={guess}
                onChange={(e) => setGuess(e.target.value)}
                placeholder="Твой ответ..."
                data-testid="crocodile-guess-input"
                className="input-cartoon flex-1 text-lg py-4 normal-case tracking-normal"
                autoComplete="off"
              />
              <button
                type="submit"
                disabled={!guess.trim()}
                data-testid="crocodile-guess-btn"
                className={`btn-neon px-6 ${guess.trim() ? "btn-success" : "bg-[#2D2D3A] text-[#A1A1AA]"}`}
              >
                <Send className="w-6 h-6" />
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
